import React, { useMemo, useState } from "react"
import { Button, Input } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"
import { TemplateCatalogueItem } from "types/template.types"
import TemplateCard from "./TemplateCard.component"
import { inputLabelAsWords, readableMerge, splitKeyword } from "./templateText"

interface TemplateGalleryProps {
  items: TemplateCatalogueItem[]
  onUse: (item: TemplateCatalogueItem) => void
  /** Set while the questionnaire is still saving: "Use template" waits and shows this text */
  useBlockedLabel?: string
  /** True when the staging tray holds blocks: its Insert is then the one solid button */
  trayHasBlocks?: boolean
}

const ALL = "All"

/**
 * The shipped categories in the order a questionnaire is built (the folders
 * of the shared Templates folder). Any other category follows alphabetically.
 */
const FIRST = [
  "Get started",
  "Screener",
  "Demographics",
  "Research sections",
  "Media and advertising",
]

/** Categories that sort last: the technical examples, then the rest. */
const LAST = ["Examples", "Other", "Your organisation"]

/** A template's identity in the catalogue (file and label, per access). */
const keyOf = (item: TemplateCatalogueItem): string =>
  `${item.access}|${item.file}|${item.template}`

/**
 * The categories with their counts: the shipped ones in `FIRST` order, then
 * any other alphabetically, then `LAST` ("Examples", "Other",
 * "Your organisation").
 *
 * @param items - The whole catalogue
 * @returns Each category once with its number of templates
 */
const countCategories = (
  items: TemplateCatalogueItem[]
): { name: string; count: number }[] => {
  const counts = new Map<string, number>()
  items.forEach((item) =>
    counts.set(item.category, (counts.get(item.category) ?? 0) + 1)
  )
  const rank = (name: string) => {
    if (FIRST.includes(name)) return FIRST.indexOf(name) - FIRST.length
    return LAST.indexOf(name) + 1
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort(
      (a, b) =>
        rank(a.name) - rank(b.name) || a.name.localeCompare(b.name)
    )
}

/**
 * The full merge text (readable, see `readableMerge`) with its keywords and
 * `{{placeholders}}` marked.
 *
 * @param props - The merge text
 * @returns The coloured text
 */
const MergeText: React.FC<{ merge: string }> = ({ merge }) => (
  <pre className="tpl-code" data-testid="template-preview-text">
    {readableMerge(merge).split(/\r?\n/).map((line, i) => {
      const { indent, keyword, rest } = splitKeyword(line)
      return (
        <React.Fragment key={i}>
          {i > 0 && "\n"}
          {indent}
          {keyword && <span className="tpl-kw">{keyword}</span>}
          {rest
            .split(/(\{\{[^}]*\}\})/)
            .map((part, j) =>
              /^\{\{[^}]*\}\}$/.test(part) ? (
                <span key={j} className="tpl-fill">
                  {part}
                </span>
              ) : (
                part
              )
            )}
        </React.Fragment>
      )
    })}
  </pre>
)

/**
 * The template gallery: categories with counts on the left, search and a
 * card grid in the middle, and the selected template in full on the right
 * with "Use template".
 *
 * @param props - The component props
 * @returns The gallery
 */
const TemplateGallery: React.FC<TemplateGalleryProps> = ({
  items,
  onUse,
  useBlockedLabel,
  trayHasBlocks = false,
}) => {
  // A template opens only once the questionnaire is saved.
  const use = (item: TemplateCatalogueItem) => {
    if (!useBlockedLabel) onUse(item)
  }
  const [category, setCategory] = useState<string>(ALL)
  const [search, setSearch] = useState<string>("")
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const categories = useMemo(() => countCategories(items), [items])

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return items.filter(
      (item) =>
        (category === ALL || item.category === category) &&
        (term === "" ||
          item.label.toLowerCase().includes(term) ||
          item.description.toLowerCase().includes(term))
    )
  }, [items, category, search])

  // The preview follows the selected card while it is shown, else the first card.
  const selected =
    shown.find((item) => keyOf(item) === selectedKey) ?? shown[0] ?? null

  const categoryButton = (name: string, count: number) => (
    <button
      key={name}
      type="button"
      className={`tpl-cat${category === name ? " tpl-cat--on" : ""}`}
      aria-pressed={category === name}
      onClick={() => setCategory(name)}
    >
      <span className="tpl-cat-name">{name}</span>
      <span className="tpl-cat-count">{count}</span>
    </button>
  )

  return (
    <div className="tpl-gallery">
      <nav className="tpl-cats" aria-label="Template categories">
        {categoryButton(ALL, items.length)}
        {categories.map(({ name, count }) => categoryButton(name, count))}
      </nav>

      <div className="tpl-main">
        <div className="tpl-search">
          <Input
            allowClear
            size="small"
            placeholder="Search templates"
            aria-label="Search templates"
            prefix={
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="text-gray-400"
              />
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {shown.length === 0 ? (
          <div className="tpl-empty">No templates match.</div>
        ) : (
          <div className="tpl-grid">
            {shown.map((item) => (
              <TemplateCard
                key={keyOf(item)}
                item={item}
                selected={selected !== null && keyOf(item) === keyOf(selected)}
                onSelect={(it) => setSelectedKey(keyOf(it))}
                onUse={use}
              />
            ))}
          </div>
        )}
      </div>

      <aside className="tpl-preview" aria-label="Template preview">
        {selected ? (
          <>
            <div className="tpl-preview-head">
              <div className="tpl-preview-title">{selected.label}</div>
              {selected.description && (
                <div className="tpl-preview-desc">{selected.description}</div>
              )}
              {selected.inputs.length > 0 && (
                <div className="tpl-preview-asks">
                  {`Asks for: ${selected.inputs
                    .map((input) => inputLabelAsWords(input.label))
                    .join(", ")}`}
                </div>
              )}
            </div>
            <MergeText merge={selected.merge} />
            <div className="tpl-preview-foot">
              <Button
                type={trayHasBlocks ? "default" : "primary"}
                disabled={!!useBlockedLabel}
                onClick={() => use(selected)}
              >
                {useBlockedLabel ?? "Use template"}
              </Button>
            </div>
          </>
        ) : (
          <div className="tpl-empty">Pick a template to see it in full.</div>
        )}
      </aside>
    </div>
  )
}

export default TemplateGallery
